// ============================================
// COMPONENT: FaultModal (Sistema Completo)
// ============================================
// Archivo: src/components/modals/FaultModal.jsx

import React, { useState, useEffect } from 'react';
import { Modal, Button } from '../common';
import { AlertTriangle, Upload, X } from 'lucide-react';
import { formatCLPInput, parseCLP } from '../../utils/formatters';

const FaultModal = ({
  show,
  fault = null,
  plant = null,
  selectedEquipmentId = null,
  equipments = [],
  onClose,
  onSubmit
}) => {
  const [formData, setFormData] = useState({
    // Básicos
    equipmentId: '',
    fechaReporte: '',
    horaInicio: '',
    tipoFalla: 'electrica',
    descripcion: '',
    equipoAfectado: '',
    componenteAfectado: '',
    impacto: 'bajo',
    accionInmediata: '',
    responsableReporte: '',
    prioridad: 'media',
    estado: 'reportada',
    estadoEquipo: 'en_falla',
    
    // OC
    numeroOc: '',
    fechaOc: '',
    montoOc: '',
    proveedor: '',
    quienApruebaOc: '',
    estadoOc: 'pendiente',
    
    // Archivos
    fileEvidencia: null,
    fileOc: null,
    filePresupuesto: null
  });
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState('basico');
  const [montoOc, setMontoOc] = useState(0);

  const isEdit = !!fault;

  useEffect(() => {
    if (fault) {
      // Modo edición - cargar datos existentes
      setFormData({
        equipmentId: fault.equipment_id || '',
        fechaReporte: fault.fecha_reporte ? new Date(fault.fecha_reporte).toISOString().split('T')[0] || '' : '',
        horaInicio: fault.hora_inicio?.split('T')[1]?.substring(0,5) || '',
        tipoFalla: fault.tipo_falla || 'electrica',
        descripcion: fault.descripcion || '',
        equipoAfectado: fault.equipo_afectado || '',
        componenteAfectado: fault.componente_afectado || '',
        impacto: fault.impacto || 'bajo',
        accionInmediata: fault.accion_inmediata || '',
        responsableReporte: fault.responsable_reporte || '',
        prioridad: fault.prioridad || 'media',
        estado: fault.estado || 'reportada',
        estadoEquipo: fault.estado_equipo_anterior || 'en_falla',
        numeroOc: fault.numero_oc || '',
        fechaOc: fault.fecha_oc || '',
        montoOc: fault.monto_oc || '',
        proveedor: fault.proveedor || '',
        quienApruebaOc: fault.quien_aprueba_oc || '',
        estadoOc: fault.estado_oc || 'pendiente',
        fileEvidencia: null,
        fileOc: null,
        filePresupuesto: null
      });
    } else {
      // Modo creación - valores por defecto
      const today = new Date().toISOString().split('T')[0];
      const now = new Date().toTimeString().substring(0,5);
      
      setFormData({
        equipmentId: selectedEquipmentId || '',
        fechaReporte: today,
        horaInicio: now,
        tipoFalla: 'electrica',
        descripcion: '',
        equipoAfectado: '',
        componenteAfectado: '',
        impacto: 'bajo',
        accionInmediata: '',
        responsableReporte: '',
        prioridad: 'media',
        estado: 'reportada',
        estadoEquipo: 'en_falla',
        numeroOc: '',
        fechaOc: '',
        montoOc: '',
        proveedor: '',
        quienApruebaOc: '',
        estadoOc: 'pendiente',
        fileEvidencia: null,
        fileOc: null,
        filePresupuesto: null
      });
    }
    setActiveTab('basico');
  }, [fault, show, selectedEquipmentId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      console.log(`Archivo ${fieldName}:`, file.name);
      setFormData(prev => ({ ...prev, [fieldName]: file }));
    }
  };

  const removeFile = (fieldName) => {
    setFormData(prev => ({ ...prev, [fieldName]: null }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.fechaReporte) newErrors.fechaReporte = 'Requerido';
    if (!formData.horaInicio) newErrors.horaInicio = 'Requerido';
    if (!formData.tipoFalla) newErrors.tipoFalla = 'Requerido';
    if (!formData.descripcion?.trim()) newErrors.descripcion = 'Requerido';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      setActiveTab('basico');
      return;
    }

    setLoading(true);

    try {
      const submitData = {
        ...formData,
        plantId: plant?.id
      };

      await onSubmit(submitData, fault?.id);
      onClose();
    } catch (error) {
      console.error('Error:', error);
      setErrors({ general: error.message || 'Error al guardar falla' });
    } finally {
      setLoading(false);
    }
  };

  // Opciones
  const tipoFallaOptions = [
    { value: 'electrica', label: 'Eléctrica' },
    { value: 'mecanica', label: 'Mecánica' },
    { value: 'hidraulica', label: 'Hidráulica' },
    { value: 'neumatica', label: 'Neumática' },
    { value: 'electronica', label: 'Electrónica' },
    { value: 'software', label: 'Software' },
    { value: 'calibracion', label: 'Calibración' },
    { value: 'otra', label: 'Otra' }
  ];

  const impactoOptions = [
    { value: 'bajo', label: 'Bajo - Sin afectación operativa' },
    { value: 'medio', label: 'Medio - Afectación parcial' },
    { value: 'alto', label: 'Alto - Operación detenida' },
    { value: 'critico', label: 'Crítico - Riesgo de seguridad' }
  ];

  const prioridadOptions = [
    { value: 'baja', label: 'Baja' },
    { value: 'media', label: 'Media' },
    { value: 'alta', label: 'Alta' },
    { value: 'urgente', label: 'Urgente' }
  ];

  const estadoFallaOptions = [
    { value: 'reportada', label: 'Reportada' },
    { value: 'en_revision', label: 'En Revisión' },
    { value: 'esperando_repuesto', label: 'Esperando Repuesto' },
    { value: 'resuelta', label: 'Resuelta' }
  ];

  const estadoEquipoOptions = [
    { value: 'operativo', label: 'Operativo' },
    { value: 'en_falla', label: 'En Falla' },
    { value: 'deshabilitado', label: 'Deshabilitado' }
  ];

  const estadoOcOptions = [
    { value: 'pendiente', label: 'Pendiente' },
    { value: 'aprobada', label: 'Aprobada' },
    { value: 'rechazada', label: 'Rechazada' },
    { value: 'en_proceso', label: 'En Proceso' },
    { value: 'completada', label: 'Completada' }
  ];

  return (
    <Modal
      show={show}
      onClose={onClose}
      title={isEdit ? 'Actualizar Falla' : 'Reportar Falla'}
      maxWidth="4xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Información de la planta */}
        {plant && (
          <div className="bg-slate-700 p-4 rounded-lg flex items-center gap-3">
            <AlertTriangle className="text-red-400" size={24} />
            <div>
              <p className="text-white font-semibold">{plant.name}</p>
              <p className="text-sm text-slate-400">{plant.code}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-600">
          <button
            type="button"
            onClick={() => setActiveTab('basico')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'basico'
                ? 'text-white border-b-2 border-red-500'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Información Básica
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('oc')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'oc'
                ? 'text-white border-b-2 border-red-500'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Orden de Compra
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('archivos')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'archivos'
                ? 'text-white border-b-2 border-red-500'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Documentos
          </button>
        </div>

        {/* Error general */}
        {errors.general && (
          <div className="bg-red-500/10 border border-red-500 text-red-400 px-4 py-2 rounded">
            {errors.general}
          </div>
        )}

        {/* TAB: Información Básica */}
        {activeTab === 'basico' && (
          <div className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              {/* Fecha */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Fecha <span className="text-red-400">*</span>
                </label>
                <input
                  type="date"
                  name="fechaReporte"
                  value={formData.fechaReporte}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white"
                />
                {errors.fechaReporte && <p className="mt-1 text-sm text-red-400">{errors.fechaReporte}</p>}
              </div>

              {/* Hora */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Hora Inicio <span className="text-red-400">*</span>
                </label>
                <input
                  type="time"
                  name="horaInicio"
                  value={formData.horaInicio}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white"
                />
                {errors.horaInicio && <p className="mt-1 text-sm text-red-400">{errors.horaInicio}</p>}
              </div>

              {/* Equipo */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Equipo Afectado
                </label>
                <select
                  name="equipmentId"
                  value={formData.equipmentId}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white"
                >
                  <option value="">General de planta</option>
                  {equipments.map(eq => (
                    <option key={eq.id} value={eq.id}>
                      {eq.name} - {eq.line}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {/* Tipo de falla */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Tipo de Falla <span className="text-red-400">*</span>
                </label>
                <select
                  name="tipoFalla"
                  value={formData.tipoFalla}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white"
                >
                  {tipoFallaOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Prioridad */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Prioridad
                </label>
                <select
                  name="prioridad"
                  value={formData.prioridad}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white"
                >
                  {prioridadOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Descripción */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Descripción de la Falla <span className="text-red-400">*</span>
              </label>
              <textarea
                name="descripcion"
                value={formData.descripcion}
                onChange={handleChange}
                rows={4}
                required
                placeholder="Describa detalladamente la falla..."
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400"
              />
              {errors.descripcion && <p className="mt-1 text-sm text-red-400">{errors.descripcion}</p>}
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {/* Componente afectado */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Componente/Parte Afectada
                </label>
                <input
                  type="text"
                  name="componenteAfectado"
                  value={formData.componenteAfectado}
                  onChange={handleChange}
                  placeholder="Ej: Motor principal, Sensor..."
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400"
                />
              </div>

              {/* Impacto */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Impacto Operacional
                </label>
                <select
                  name="impacto"
                  value={formData.impacto}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white"
                >
                  {impactoOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Acción inmediata */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Acción Inmediata Tomada
              </label>
              <textarea
                name="accionInmediata"
                value={formData.accionInmediata}
                onChange={handleChange}
                rows={3}
                placeholder="Describa las acciones inmediatas..."
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400"
              />
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              {/* Responsable */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Responsable del Reporte
                </label>
                <input
                  type="text"
                  name="responsableReporte"
                  value={formData.responsableReporte}
                  onChange={handleChange}
                  placeholder="Nombre"
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400"
                />
              </div>

              {/* Estado de la falla */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Estado de la Falla
                </label>
                <select
                  name="estado"
                  value={formData.estado}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white"
                >
                  {estadoFallaOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Estado del equipo */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Estado del Equipo
                </label>
                <select
                  name="estadoEquipo"
                  value={formData.estadoEquipo}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white"
                >
                  {estadoEquipoOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* TAB: Orden de Compra */}
        {activeTab === 'oc' && (
          <div className="space-y-4">
            <div className="bg-blue-500/10 border border-blue-500 text-blue-400 px-4 py-3 rounded">
              <p className="text-sm">Complete esta sección si la falla requiere una orden de compra para repuestos o servicios.</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {/* Número de OC */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Número de OC
                </label>
                <input
                  type="text"
                  name="numeroOc"
                  value={formData.numeroOc}
                  onChange={handleChange}
                  placeholder="Ej: OC-2024-001"
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400"
                />
              </div>

              {/* Fecha OC */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Fecha de OC
                </label>
                <input
                  type="date"
                  name="fechaOc"
                  value={formData.fechaOc}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {/* Monto */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Monto OC (CLP)
                </label>
                <input
                  type="text"  // ✅
                  value={formatCLPInput(montoOc.toString())}
                  onChange={(e) => {
                    const formatted = formatCLPInput(e.target.value);
                    const numeric = parseCLP(formatted);
                    setMontoOc(numeric);
                  }}
                  placeholder="0"
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400"
                />
              </div>

              {/* Estado OC */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Estado de la OC
                </label>
                <select
                  name="estadoOc"
                  value={formData.estadoOc}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white"
                >
                  {estadoOcOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Proveedor */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Proveedor
              </label>
              <input
                type="text"
                name="proveedor"
                value={formData.proveedor}
                onChange={handleChange}
                placeholder="Nombre del proveedor"
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400"
              />
            </div>

            {/* Quien aprueba */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Quien Aprueba la OC
              </label>
              <input
                type="text"
                name="quienApruebaOc"
                value={formData.quienApruebaOc}
                onChange={handleChange}
                placeholder="Nombre del aprobador"
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400"
              />
            </div>
          </div>
        )}

        {/* TAB: Documentos */}
        {activeTab === 'archivos' && (
          <div className="space-y-4">
            {/* Evidencia */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Evidencia de la Falla (Foto/PDF)
              </label>
              <div className="flex items-center gap-3">
                <label className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-3 px-4 py-3 bg-slate-700 border-2 border-dashed border-slate-600 rounded-lg hover:border-red-500 transition-colors">
                    <Upload size={20} className="text-slate-400" />
                    <span className="text-slate-400">
                      {formData.fileEvidencia ? formData.fileEvidencia.name : 'Seleccionar archivo...'}
                    </span>
                  </div>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'fileEvidencia')}
                    className="hidden"
                  />
                </label>
                {formData.fileEvidencia && (
                  <button
                    type="button"
                    onClick={() => removeFile('fileEvidencia')}
                    className="p-2 bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    <X size={20} />
                  </button>
                )}
              </div>
            </div>

            {/* OC */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Orden de Compra (PDF)
              </label>
              <div className="flex items-center gap-3">
                <label className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-3 px-4 py-3 bg-slate-700 border-2 border-dashed border-slate-600 rounded-lg hover:border-blue-500 transition-colors">
                    <Upload size={20} className="text-slate-400" />
                    <span className="text-slate-400">
                      {formData.fileOc ? formData.fileOc.name : 'Seleccionar OC...'}
                    </span>
                  </div>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => handleFileChange(e, 'fileOc')}
                    className="hidden"
                  />
                </label>
                {formData.fileOc && (
                  <button
                    type="button"
                    onClick={() => removeFile('fileOc')}
                    className="p-2 bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    <X size={20} />
                  </button>
                )}
              </div>
            </div>

            {/* Presupuesto */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Presupuesto (PDF)
              </label>
              <div className="flex items-center gap-3">
                <label className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-3 px-4 py-3 bg-slate-700 border-2 border-dashed border-slate-600 rounded-lg hover:border-green-500 transition-colors">
                    <Upload size={20} className="text-slate-400" />
                    <span className="text-slate-400">
                      {formData.filePresupuesto ? formData.filePresupuesto.name : 'Seleccionar presupuesto...'}
                    </span>
                  </div>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => handleFileChange(e, 'filePresupuesto')}
                    className="hidden"
                  />
                </label>
                {formData.filePresupuesto && (
                  <button
                    type="button"
                    onClick={() => removeFile('filePresupuesto')}
                    className="p-2 bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    <X size={20} />
                  </button>
                )}
              </div>
            </div>

            <div className="bg-slate-700 p-4 rounded-lg">
              <p className="text-sm text-slate-400">
                📎 Formatos aceptados: PDF, JPG, PNG<br />
                💾 Tamaño máximo: 5MB por archivo
              </p>
            </div>
          </div>
        )}

        {/* Botones */}
        <div className="flex justify-between items-center pt-4 border-t border-slate-600">
          <div className="text-sm text-slate-400">
            {activeTab === 'basico' && '1/3'}
            {activeTab === 'oc' && '2/3'}
            {activeTab === 'archivos' && '3/3'}
          </div>
          
          <div className="flex gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-red-600 hover:bg-red-700"
            >
              {loading ? 'Guardando...' : (isEdit ? 'Actualizar Falla' : 'Reportar Falla')}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default FaultModal;
